import {
  ActionIcon,
  Box,
  Button,
  Card,
  Flex,
  Group,
  Loader,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
  MultiSelect,
  Select,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { IconCalendarEvent, IconClipboardText, IconX } from '@tabler/icons-react';
import { useMemo, useCallback, useRef, useEffect } from 'react';
import { Encounter, Task } from '@medplum/fhirtypes';
import { EncounterTimelineComponent } from './EncounterTimelineComponent';
import { useTimelineContext } from '@/hooks/useTimelineContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useCreateEncounter } from '@/pages/PatientTimelinePage/hooks/useCreateEncounter';
import { getPatientName } from '@/pages/PatientTimelinePage/services/timelinePatient';
import {
  STANDALONE_PATIENT_ACCESS_CARD_KINDS,
  standalonePatientAccessCardKindFor,
} from '@/components/Timeline/TimelineItems/PatientAccessCall/patientAccessCardKind';
import { useCreatePatientAccessCard } from '@/components/Timeline/TimelineItems/PatientAccessCall/useCreatePatientAccessCard';

export function EncounterChartBox({
  handleNavigateToItem,
}: {
  handleNavigateToItem?: (itemReference: string) => void;
}) {
  const { patientContext, messageBoxContext } = useTimelineContext();
  const {
    currentUserPermissions: { canCreatePatientAccessNote, canViewClinicalRecord },
  } = useCurrentUser();
  const {
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    selectedPlanDefinition,
    setSelectedPlanDefinition,
    planDefinitions,
    isLoadingPlanDefinitions,
    isCreating,
    encounter,
    tasks,
    handleCreateEncounter,
    isLoadingEncounterDetails,
    isEditingExistingEncounter,
    clearEncounterDraft,
    isLoadingAppointments,
    appointmentsFieldset,
    selectedAppointment,
    setSelectedAppointment,
  } = useCreateEncounter({ loadTemplates: canViewClinicalRecord });
  const { startCard, isCreating: isCreatingCard } = useCreatePatientAccessCard();

  const patient = patientContext?.patient;
  const { setShowMessageInput } = messageBoxContext;

  const patientName = useMemo(() => (patient ? getPatientName(patient) : 'Unknown Patient'), [patient]);

  const planDefinitionOptions = useMemo(() => {
    return planDefinitions.map((pd) => ({
      value: `PlanDefinition/${pd.id}`,
      label: pd.title || pd.name || `PlanDefinition/${pd.id}`,
    }));
  }, [planDefinitions]);

  // A card is a Communication, not a PlanDefinition, so it needs its own branch below
  // rather than a template — but it reads as one more note to pick, so it sits in the
  // same flat list.
  // The gate on the chart lives here, not on the menu item that opens this box:
  // a role that never charts an Encounter (Patient Access, BAR-2585) still starts
  // its own call cards from this same picker.
  const chartNoteOptions = useMemo(() => {
    const templates = canViewClinicalRecord ? planDefinitionOptions : [];
    if (!canCreatePatientAccessNote) return templates;

    return [...templates, ...STANDALONE_PATIENT_ACCESS_CARD_KINDS.map(({ value, label }) => ({ value, label }))];
  }, [planDefinitionOptions, canCreatePatientAccessNote, canViewClinicalRecord]);

  const selectedCardKind = standalonePatientAccessCardKindFor(selectedPlanDefinition);

  const handleClose = useCallback(() => {
    clearEncounterDraft();
    setShowMessageInput(false);
  }, [clearEncounterDraft, setShowMessageInput]);

  const handleCreate = useCallback(async () => {
    if (!selectedCardKind) {
      if (!canViewClinicalRecord) {
        return;
      }
      await handleCreateEncounter();
      return;
    }

    // The card lands on the timeline and focuses itself; there is no Encounter to
    // stay open for. The linked appointment becomes the card's `basedOn`, which is
    // what lets that appointment's Chart row find it instead of offering "Start …"
    // again and creating a second card.
    await startCard(selectedCardKind, selectedAppointment ? { reference: selectedAppointment } : undefined);
    handleClose();
  }, [selectedCardKind, selectedAppointment, handleCreateEncounter, startCard, handleClose, canViewClinicalRecord]);

  const encounterChartBoxRef = useRef<HTMLDivElement>(null);

  // Scroll to the component when editing an existing encounter
  useEffect(() => {
    if (
      isEditingExistingEncounter &&
      encounterChartBoxRef.current &&
      !isLoadingEncounterDetails &&
      !isLoadingAppointments
    ) {
      // Use setTimeout to ensure the component is fully rendered
      setTimeout(() => {
        encounterChartBoxRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }, 100);
    }
  }, [isEditingExistingEncounter, isLoadingEncounterDetails]);

  if (isLoadingEncounterDetails) {
    return (
      <div ref={encounterChartBoxRef}>
        <Card withBorder padding={0} radius="lg" bg="white">
          <Stack align="center" justify="center" p="xl" gap="sm">
            <Loader color="blue" />
            <Text c="dimmed" size="sm">
              Loading chart details...
            </Text>
          </Stack>
        </Card>
      </div>
    );
  }

  if (encounter?.id && tasks.length > 0) {
    return (
      <div ref={encounterChartBoxRef}>
        <EncounterTimelineComponent
          encounter={encounter as Encounter}
          tasks={tasks as Task[]}
          patientName={patientName}
          onSaveDraft={handleClose}
          onSignEncounter={handleClose}
          handleNavigateToItem={handleNavigateToItem}
        />
      </div>
    );
  }

  return (
    <div ref={encounterChartBoxRef}>
      <Card withBorder padding={0} radius="lg" bg="white">
        <Box>
          <Group w="100%" p="xs" justify="space-between" align="center">
            <Group>
              <ThemeIcon variant="filled" radius="lg" size="lg" bg="blue.6">
                <IconClipboardText size={16} />
              </ThemeIcon>
              <Text variant="header">
                {isEditingExistingEncounter ? `Editing chart for ${patientName}` : `New chart note for ${patientName}`}
              </Text>
            </Group>
            <Tooltip label="Close" withArrow position="top-end" openDelay={500}>
              <ActionIcon variant="subtle" c="gray.6" size="md" radius="xl" aria-label="Close" onClick={handleClose}>
                <IconX size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Box p="sm" style={{ borderTop: '1px solid gray.2' }}>
            <Stack gap="md">
              <Group>
                <Select
                  w="100%"
                  clearable
                  disabled={isCreating}
                  defaultValue={selectedAppointment}
                  value={selectedAppointment}
                  label="Link to an appointment"
                  placeholder="Select an appointment"
                  onChange={setSelectedAppointment}
                  nothingFoundMessage="No appointments found"
                  data={[
                    { group: 'Past appointments', items: appointmentsFieldset.past },
                    { group: 'Future appointments', items: appointmentsFieldset.future },
                  ]}
                  searchable
                />
              </Group>
              <Group grow display={selectedCardKind ? 'none' : undefined}>
                <Box flex={1}>
                  <DateTimePicker
                    required
                    label="Visit start time"
                    placeholder="Select start time"
                    valueFormat="MMM D, YYYY, h:mma"
                    value={startTime}
                    onChange={(value) => setStartTime(value ? new Date(value) : null)}
                    disabled={isCreating || !!selectedAppointment}
                    clearable
                    rightSection={<IconCalendarEvent size={16} />}
                  />
                </Box>
                <Box flex={1}>
                  <DateTimePicker
                    required
                    label="Visit end time"
                    placeholder="Select end time"
                    valueFormat="MMM D, YYYY, h:mma"
                    value={endTime}
                    onChange={(value) => setEndTime(value ? new Date(value) : null)}
                    disabled={isCreating || !!selectedAppointment}
                    clearable
                    rightSection={<IconCalendarEvent size={16} />}
                  />
                </Box>
              </Group>
              <Box bg="gray.1" p="sm" style={{ borderRadius: '8px' }}>
                <MultiSelect
                  label="Select chart note template"
                  required
                  placeholder={isLoadingPlanDefinitions ? 'Loading templates...' : 'Select chart note template'}
                  data={chartNoteOptions}
                  value={selectedPlanDefinition ? [selectedPlanDefinition] : []}
                  onChange={(value) => setSelectedPlanDefinition(value[0])}
                  disabled={isCreating || isCreatingCard || isLoadingPlanDefinitions}
                  searchable
                  clearable
                />
              </Box>
              <Flex justify="flex-end">
                <Button
                  variant="primary"
                  radius="xl"
                  loading={isCreating || isCreatingCard}
                  disabled={!selectedPlanDefinition}
                  onClick={handleCreate}
                >
                  {selectedCardKind ? 'Start card' : 'Create chart note'}
                </Button>
              </Flex>
            </Stack>
          </Box>
        </Box>
      </Card>
    </div>
  );
}
